import { ZoomOutMap } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import React, { ReactElement } from "react";

import { RenderingObjectKind } from "../../../generated/gql/graphql";
import useLocale from "../../../hooks/use-locale";
import { Size } from "../../../types/size";
import { Vector2 } from "../../../types/vector";

import { CanvasObject } from "./canvas-object";

// Paddings to use (in %)
const padding = { left: 2, right: 2, top: 4.57, bottom: 6.57 };

export interface SpanPackshotButtonProps {
  canvasSize: Size;
  canvasObject: CanvasObject | undefined;
  canvasObjectChange: (changes: Partial<CanvasObject>) => void;
}

export default function SpanPackshotButton({
  canvasSize,
  canvasObject,
  canvasObjectChange,
}: SpanPackshotButtonProps): ReactElement {
  const { getText } = useLocale();

  function handleSpanPackshotClick(): void {
    if (canvasObject?.kind !== RenderingObjectKind.Presentation) {
      return;
    }

    if (!canvasObject.presentation) {
      return;
    }

    const assetWidth = canvasObject.presentation.asset?.width;
    const assetHeight = canvasObject.presentation.asset?.height;
    if (!assetWidth || !assetHeight) {
      return;
    }

    const canvasResolution = canvasSize.width;

    // Calculate asset size on canvas based on reference resolution
    let assetCanvasWidth = assetWidth;
    let assetCanvasHeight = assetHeight;

    // Consider reference resolution of presentation
    const referenceResolution = canvasObject.presentation.referenceResolution;
    assetCanvasWidth *= canvasResolution / referenceResolution;
    assetCanvasHeight *= canvasResolution / referenceResolution;

    // Calculate available area, defined by canvas size and padding
    const maxWidth =
      (canvasSize.width * (100 - padding.left - padding.right)) / 100;
    const maxHeight =
      (canvasSize.height * (100 - padding.top - padding.bottom)) / 100;

    // Calculate scale (use maximum scale that stays in defined area)
    const scaleX = maxWidth / assetCanvasWidth;
    const scaleY = maxHeight / assetCanvasHeight;
    const scale = Math.min(scaleX, scaleY);

    // Calculate offset (should be placed horizontally center and vertically bottom)
    const packshotWidth = assetCanvasWidth * scale;
    const packshotHeight = assetCanvasHeight * scale;
    const offset: Vector2 = {
      x: Math.round((canvasSize.width - packshotWidth) / 2),
      y: Math.round(
        canvasSize.height -
          (padding.bottom / 100) * canvasSize.height -
          packshotHeight,
      ),
    };

    canvasObjectChange({ scale, offset });
  }

  return (
    <Tooltip title={getText("sections.canvas.header.spanPackshot.tooltip")}>
      <IconButton
        disabled={canvasObject?.kind !== RenderingObjectKind.Presentation}
        onClick={() => handleSpanPackshotClick()}
      >
        <ZoomOutMap />
      </IconButton>
    </Tooltip>
  );
}
